import { useEffect, useState } from 'react';
import { useQuery } from 'convex/react';

import { api } from '../../convex/_generated/api';

/**
 * Is the FastAPI orchestrator alive?
 *
 * The backend writes a heartbeat row into Convex every ~10 s. We read it
 * reactively and compare against a local clock that ticks every 5 s, so
 * the pipeline page can warn the participant when the backend is down
 * instead of leaving them staring at a spinner.
 */
export interface BackendLiveness {
  /** null until the first heartbeat row exists. */
  lastPollAt: number | null;
  ageMs: number | null;
  /** True when a heartbeat arrived within the last 45 s. */
  alive: boolean;
  /** Experimenter paused the orchestrator. */
  paused: boolean;
  loaded: boolean;
}

const STALE_AFTER_MS = 45_000;

export function useBackendLiveness(): BackendLiveness {
  const row = useQuery(api.admin.backendLiveness, {});
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 5_000);
    return () => clearInterval(id);
  }, []);

  if (row === undefined) {
    return { lastPollAt: null, ageMs: null, alive: true, paused: false, loaded: false };
  }
  const ageMs = row.lastPollAt === null ? null : now - row.lastPollAt;
  return {
    lastPollAt: row.lastPollAt,
    ageMs,
    alive: ageMs !== null && ageMs < STALE_AFTER_MS,
    paused: row.paused,
    loaded: true,
  };
}
